import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcSortingBoxDefDto {
    id: number;
    companyNo: string;
    stcBoxTypeID: string;
    description1?: string | null;
    width?: number | null;
    depth?: number | null;
    height?: number | null;
    totalVolume?: number | null;
    updatedBy?: string | null;
    lastUpdate?: string | null;
    createdBy?: string | null;
    created?: string | null;
}

export interface CreateStcSortingBoxDefRequest {
    companyNo: string;
    stcBoxTypeID: string;
    description1?: string | null;
    width?: number | null;
    depth?: number | null;
    height?: number | null;
    totalVolume?: number | null;
}

export interface UpdateStcSortingBoxDefRequest {
    description1?: string | null;
    width?: number | null;
    depth?: number | null;
    height?: number | null;
    totalVolume?: number | null;
}

export interface BulkUpdateStcSortingBoxDefRequest {
    companyNo: string;
    stcBoxTypeID: string;
    description1?: string | null;
    width?: number | null;
    depth?: number | null;
    height?: number | null;
    totalVolume?: number | null;
}

class StcSortingBoxDefService {
    private baseUrl = `${API_BASE_URL}/stc-sorting-box-defs`;

    async getCurrent(): Promise<StcSortingBoxDefDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<StcSortingBoxDefDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByCompanyNo(companyNo: string): Promise<StcSortingBoxDefDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByCompanyAndBoxTypeId(companyNo: string, stcBoxTypeId: string): Promise<StcSortingBoxDefDto> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${stcBoxTypeId}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateStcSortingBoxDefRequest): Promise<StcSortingBoxDefDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async update(companyNo: string, stcBoxTypeId: string, request: UpdateStcSortingBoxDefRequest): Promise<StcSortingBoxDefDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${stcBoxTypeId}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateStcSortingBoxDefRequest[]): Promise<StcSortingBoxDefDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async delete(companyNo: string, stcBoxTypeId: string): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${stcBoxTypeId}`);
            return response.ok;
        } catch (error) {
            console.error('Error deleting sorting box definition:', error);
            return false;
        }
    }

    async bulkDelete(ids: number[]): Promise<{ deletedCount: number; failedCount: number; errors: string[] }> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async exists(companyNo: string, stcBoxTypeId: string): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${stcBoxTypeId}`);
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    async search(companyNo: string, searchTerm: string): Promise<StcSortingBoxDefDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }
}

export const stcSortingBoxDefService = new StcSortingBoxDefService();
